import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {Strand} from 'constants/MolbioConstants';
import {ANNOTATION_MAX_NOTES_LENGTH} from 'constants/SequenceConstants';
import AnnotationTypeDropdownInput from 'molbio/annotations/AnnotationTypeDropdownInput';
import {AnnotationData} from 'molbio/annotations/AnnotationUtil';
import IndexSelectionGroup from 'molbio/IndexSelectionGroup';
import CustomFieldsForm from 'molbio/sequence-features/CustomFieldsForm';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import StrandDropdown from 'molbio/sequence-features/StrandDropdown';
import {BenchlingDispatch} from 'redux-stores/Store';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  focusAnnotationFormComplete,
  updateAnnotationEditData,
} from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import {
  AnnotationPanelEditData,
  useAnnotationPanelUIBySequence,
} from 'toolbar/feature-panel/reduceFeaturePanelUI';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import {isEnterKey} from 'util/Keyboard';

import {SaveAnnotationData} from './AnnotationPanelUtil';

interface ActionProps {
  updateEditFormData: (sequenceId: string, data: Partial<AnnotationPanelEditData>) => void;
}

export const stubbableFunctions = {
  mapDispatchToProps(dispatch: BenchlingDispatch): ActionProps {
    return {
      updateEditFormData: (sequenceId: string, data: Partial<AnnotationPanelEditData>) => {
        dispatch(updateAnnotationEditData(sequenceId, data));
      },
    };
  },
};

export interface CreateEditAnnotationFormProps {
  initialAnnotationData: AnnotationData | null;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  isSingleStranded: boolean;
  allAnnotationTypes: Array<string>;
  allAnnotationFieldKeys: Array<string>;
  onClickSave: (data: SaveAnnotationData) => void;
  onClickCancel: () => void;
  isReadOnly: boolean;
}

function CreateEditAnnotationForm({
  initialAnnotationData,
  sequenceId,
  sequenceStartIndex,
  sequenceLength,
  isSingleStranded,
  allAnnotationTypes,
  allAnnotationFieldKeys,
  onClickSave,
  onClickCancel,
  isReadOnly,
}: CreateEditAnnotationFormProps): JSX.Element {
  const {isCreateActive, shouldFocus, ...annotationEditData} = useAnnotationPanelUIBySequence(sequenceId);
  const dispatch = useDispatch<BenchlingDispatch>();
  const {updateEditFormData} = stubbableFunctions.mapDispatchToProps(dispatch);
  const nameField = useRef<TextInput>(null);

  useEffect(() => {
    // Sometimes we race with other less important focus actions, so setTimeout so we run last.
    setTimeout(() => {
      if (nameField.current && shouldFocus) {
        nameField.current.focus();
        dispatch(focusAnnotationFormComplete(sequenceId));
      }
    }, 0);
  }, [shouldFocus, dispatch, sequenceId]);

  return (
    <div
      className={css(styles.formContainer)}
      onKeyDown={(e) => {
        if (!isReadOnly && isEnterKey(e)) {
          onClickSave(annotationEditData);
        }
      }}
      {...getTestProps({component: 'CreateEditAnnotationForm', element: 'root'})}
    >
      {isCreateActive && <FieldLabel className={css(styles.newAnnotationTitle)} label="New annotation" />}
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Name" />
        <TextInput
          className={css(styles.fieldInput)}
          placeholder="Annotation name"
          value={annotationEditData.name}
          onChange={(e) => {
            updateEditFormData(sequenceId, {name: e.target.value});
          }}
          onBlur={() => {
            if (initialAnnotationData?.name !== annotationEditData.name) {
              analytics.track('annotation', 'edited_name', {
                product: ANALYTICS_MODULE.MOLBIO,
                sequence_id: sequenceId,
              });
            }
          }}
          ref={nameField}
          disabled={isReadOnly}
          dataTestAttrs={{component: 'CreateEditAnnotationForm', element: 'nameInput'}}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Position" />
        <IndexSelectionGroup
          endIndex={annotationEditData.end}
          sequenceLength={sequenceLength}
          sequenceStartIndex={sequenceStartIndex}
          startIndex={annotationEditData.start}
          updateEndIndex={(endIndex: number) => {
            updateEditFormData(sequenceId, {
              end: endIndex,
            });
          }}
          updateStartIndex={(startIndex: number) => {
            updateEditFormData(sequenceId, {
              start: startIndex,
            });
          }}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Annotation type" />
        <AnnotationTypeDropdownInput
          className={css(styles.fieldInput)}
          type={annotationEditData.annotationType}
          allTypes={allAnnotationTypes}
          onChangeType={(type: string): void => {
            updateEditFormData(sequenceId, {annotationType: type});
          }}
          onBlur={() => {
            if (initialAnnotationData?.annotation_type !== annotationEditData.annotationType) {
              analytics.track('annotation', 'edited_annotation_type', {
                product: ANALYTICS_MODULE.MOLBIO,
                sequence_id: sequenceId,
              });
            }
          }}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Color" />
        <PopoverColorPicker
          className={css(styles.fieldInput)}
          selectedColor={annotationEditData.color}
          onSelectColor={(color) => {
            updateEditFormData(sequenceId, {color});
          }}
          asDropdown={true}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Strand" />
        <StrandDropdown
          className={css(styles.fieldInput)}
          strand={annotationEditData.strand as Strand}
          onSelectStrand={(value: Strand): void => {
            updateEditFormData(sequenceId, {strand: value});
          }}
          disabled={isSingleStranded || isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.notesLabel)} label="Notes" />
        <textarea
          className={css(styles.fieldInput)}
          placeholder="Notes"
          maxLength={ANNOTATION_MAX_NOTES_LENGTH}
          value={annotationEditData.notes}
          onChange={(e) => {
            updateEditFormData(sequenceId, {notes: e.target.value});
          }}
          rows={annotationEditData.notes ? 3 : 1}
          disabled={isReadOnly}
        />
      </FormGroup>

      <CustomFieldsForm
        customFields={annotationEditData.customFields}
        setCustomFields={(newFields) => {
          updateEditFormData(sequenceId, {customFields: newFields});
        }}
        allCustomFieldKeys={allAnnotationFieldKeys}
        emptyFieldsMessage="This annotation has no custom fields"
        isReadOnly={isReadOnly}
      />

      <div className={css(styles.buttonRow)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={onClickCancel}
          text="Cancel"
        />
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={() => {
            onClickSave(annotationEditData);
          }}
          text="Save"
          disabled={isReadOnly}
          dataTestAttrs={{component: 'CreateEditAnnotationForm', element: 'saveButton'}}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  newAnnotationTitle: {
    marginBottom: '16px',
  },
  formContainer: {
    padding: '12px',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingTop: '16px',
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  fieldInput: {
    width: '172px',
  },
  fieldInputLabel: {
    margin: 'auto 0',
  },
  notesLabel: {
    marginTop: 6,
  },
});

export default CreateEditAnnotationForm;
